import { ChangeDetectionStrategy, Component, DestroyRef, LOCALE_ID, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { differenceInSeconds, formatDuration, intervalToDuration, isValid, parseISO } from 'date-fns';
import { enUS, it, type Locale } from 'date-fns/locale';

type Direction = 'future' | 'past';
type Unit = 'days' | 'hours' | 'minutes' | 'seconds';
type DisplayState = { state: 'invalid'; aria: string } | { state: 'now'; aria: string } | { state: 'ok'; direction: Direction; text: string; aria: string };

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;

const DATE_FNS_LOCALES: Record<string, Locale> = {
  it,
  'it-IT': it,
  en: enUS,
  'en-US': enUS
};

@Component({
  selector: 'app-time-delta',
  standalone: true,
  imports: [],
  templateUrl: './time-delta.component.html',
  styleUrl: './time-delta.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeDeltaComponent {
  readonly target = input<Date | null, string | Date | null | undefined>(null, {
    transform: (value: string | Date | null | undefined): Date | null => this.#coerceDate(value)
  });

  readonly #now = signal(new Date());
  #timerId: number | null = null;

  readonly #localeId = inject(LOCALE_ID);
  readonly #dateFnsLocale = computed<Locale>(() => {
    const base = this.#localeId.split('-')[0];
    return DATE_FNS_LOCALES[this.#localeId] ?? DATE_FNS_LOCALES[base] ?? enUS;
  });

  readonly display = computed<DisplayState>(() => {
    const target = this.target();
    if (!target) {
      return { state: 'invalid', aria: 'Target date is not set or invalid.' };
    }

    const now = this.#now();
    const diffSeconds = differenceInSeconds(target, now);
    const absSeconds = Math.abs(diffSeconds);

    if (absSeconds === 0) {
      return { state: 'now', aria: 'Now' };
    }

    const direction: Direction = diffSeconds >= 0 ? 'future' : 'past';
    const units = this.#unitsFor(absSeconds);
    const duration = intervalToDuration({
      start: direction === 'future' ? now : target,
      end: direction === 'future' ? target : now
    });
    const text = formatDuration(duration, {
      format: units,
      delimiter: ' ',
      zero: true,
      locale: this.#dateFnsLocale()
    });

    const aria = direction === 'future' ? `in ${text}` : `${text} ago`;

    return { state: 'ok', direction, text, aria };
  });

  constructor(destroyRef: DestroyRef) {
    effect(() => {
      this.target();
      this.#scheduleTick();
    });

    destroyRef.onDestroy(() => this.#clearTimer());
  }

  #unitsFor(absSeconds: number): Unit[] {
    if (absSeconds >= 48 * 3600)
      return ['days', 'hours', 'minutes'];
    if (absSeconds >= 3600)
      return ['hours', 'minutes'];
    return ['minutes', 'seconds'];
  }

  #scheduleTick() {
    this.#clearTimer();
    const target = this.target();
    if (!target) {
      return;
    }

    const now = untracked(() => this.#now());
    const absSeconds = Math.abs(differenceInSeconds(target, now));
    const units = this.#unitsFor(absSeconds);
    const tickMs = units.includes('seconds') ? SECOND_MS : MINUTE_MS;

    this.#timerId = window.setTimeout(() => {
      this.#now.set(new Date());
      this.#scheduleTick();
    }, tickMs);
  }

  #clearTimer() {
    if (this.#timerId !== null) {
      clearTimeout(this.#timerId);
      this.#timerId = null;
    }
  }

  #coerceDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? new Date(value.getTime()) : null;
    const iso = parseISO(value);
    if (iso && isValid(iso)) return iso;
    const parsed = new Date(value);
    return isValid(parsed) ? parsed : null;
  }
}
